import type { IntlShape } from 'react-intl'

import type { definitions } from '@luxuryescapes/contract-trip'

export type PublicAccommodationPricing = definitions['accommodationPricingPublic']

export type AccommodationUnavailableReason = NonNullable<PublicAccommodationPricing['unavailableReason']>

export interface DisplayPricing {
  price?: number
  luxPlusPrice?: number
  value?: number
  memberValue?: number
  discountPercent?: number
  memberDiscountPercent?: number
  currencyCode?: string
}

export const displayPricingFromDTO = (pricing: PublicAccommodationPricing): DisplayPricing => ({
  price: pricing.amount,
  luxPlusPrice: pricing.memberAmount,
  value: pricing.value,
  memberValue: pricing.memberValue,
  discountPercent: pricing.discountPercent,
  memberDiscountPercent: pricing.memberDiscountPercent,
  currencyCode: pricing.currencyCode,
})

export const isTotalMode = (mode: PublicAccommodationPricing['mode']): boolean =>
  mode === 'selected_rate'

export const includesTaxesAndFees = (pricing: PublicAccommodationPricing): boolean =>
  pricing.amountIncludes === 'all_in'

export const pricingUnavailableReason = (
  pricing?: PublicAccommodationPricing,
): AccommodationUnavailableReason | undefined =>
  pricing != null && pricing.amount === undefined ? pricing.unavailableReason ?? 'unavailable' : undefined

export const getAccommodationUnavailableLabel = (
  reason: AccommodationUnavailableReason,
  intl: IntlShape,
): string => {
  if (reason === 'sold_out') {
    return intl.formatMessage({
      id: '5qRgRGxub1Uhedt_JSwD',
      defaultMessage: 'Sold Out',
      description: 'Label shown when a hotel tile is sold out in the trip planner AI chat',
    })
  }

  // Nothing is wrong with the property — the stay just has no dates to price against, and
  // Always-On and bedbank rates are withheld without them. Say what would fix it rather
  // than the generic "unavailable", which reads as though the hotel itself were unbookable.
  if (reason === 'dates_required') {
    return intl.formatMessage({
      id: 'eW-Ec76KDd0hMiRHAbFd',
      defaultMessage: 'Select dates',
      description: 'Label shown in place of a hotel price in the trip planner when the stay has no dates yet, so the rate cannot be resolved',
    })
  }

  return intl.formatMessage({
    id: '3tjsFVSoCaUB1fkmuBtg',
    defaultMessage: 'Price unavailable',
    description: 'Label shown when hotel tile pricing is unavailable in the trip planner AI chat',
  })
}
